import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { userState } from "@/global/store/data/user";
import { useAppStore } from "@/global/store/store";
import { useDispatch } from "react-redux";
import { api } from "../api/api";
import { useTeamConfig } from "../hooks/useTeamConfig";
import { useTicketmaster } from "../hooks/useTicketmaster";
import {
  authOriginUIDs,
  authUIDs,
  sessionLocalStorageKey,
  vendorUUIDs,
} from "../config/constants";
import { routes } from "../routes";
import { useRouter } from "next/router";
import { useMutation } from "@tanstack/react-query";
import { userPingApi } from "../api/auth";
import { createUrlFromNextRouter, objectToParamString } from "../utils/utils";
import { SessionTimeoutModal } from "@/shared/modules/SessionTimeoutModal/SessionTimeoutModal";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { useInternalAuth } from "../hooks/useInternalAuth";
import { globalConfig } from "../config/global_config";
import { useTicketsdotcom } from "../hooks/useTicketsdotcom";
import { handleRemoveUpdatedAccountGroup } from "@/shared/modules/PurchaseSeats/Packs/packs.utils";
import { purchaseSeatsTabUIDs } from "@/global/config/constants";
import { isAuthenticatingState } from "../store/data/isAuthenticating";

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthCtxType {
  logoutTicketMaster: (urlParams?: Record<string, any>) => void;
  logoutTicketMasterV2: (redirectUrl: string) => void;
  isLogginOutTm: boolean;
}

const AuthContext = createContext({});
export const useAuthCtx = () => useContext(AuthContext) as AuthCtxType;

export function AuthProvider({ children }: AuthProviderProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const urlHasSessionTimeoutParam = router?.query?.isSessionTimeout;

  const teamConfig = useTeamConfig();
  const {
    teamSchema,
    platform,
    clientId,
    oktaClientId,
    oktaIssuer,
    authConfigs,
  } = teamConfig;
  const user = useAppStore((s) => s.user);
  const [isLogginOutTm, setIsLogginOutTm] = useState(false);
  const sessionTimeoutModal = useToggle();
  const { handleSignOut } = useTicketsdotcom();
  const { shouldUseInternalLogin, handleInternalLogOut } = useInternalAuth();

  // ------------------- Apis ---------------------
  const userPing = useMutation(userPingApi);

  const logoutTicketMaster = async (urlParams?: Record<string, any>) => {
    setIsLogginOutTm(true);
    const PARAMS = objectToParamString(urlParams);
    const redirectUrl = `${globalConfig.frontendUrl}/${teamSchema}/landing${PARAMS}`;
    let tmUrl = `https://auth.ticketmaster.com/sign-out?clientId=${clientId}&redirectUrl=${encodeURIComponent(
      redirectUrl
    )}`;

    return new Promise((resolve) => {
      setTimeout(() => {
        localStorage.removeItem(sessionLocalStorageKey + "-" + teamSchema);
        handleRemoveUpdatedAccountGroup(teamSchema);
        const logoutEvent = new CustomEvent("userLogout");
        window?.dispatchEvent(logoutEvent);
        window.open(tmUrl, "_self");
        setIsLogginOutTm(false);
        resolve(true);
      }, 800);
    });
  };

  const logoutTicketMasterV2 = async (redirectUrl: string) => {
    let tmUrl = `https://auth.ticketmaster.com/sign-out?clientId=${clientId}&redirectUrl=${encodeURIComponent(
      redirectUrl
    )}`;

    return new Promise((resolve) => {
      setTimeout(() => {
        localStorage.removeItem(sessionLocalStorageKey + "-" + teamSchema);
        handleRemoveUpdatedAccountGroup(teamSchema);
        const logoutEvent = new CustomEvent("userLogout");
        window?.dispatchEvent(logoutEvent);
        window.open(tmUrl, "_self");
        setIsLogginOutTm(false);
        resolve(true);
      }, 800);
    });
  };

  const isChooseSeatsTabWithEventCodes = () => {
    const purchaseSeatTab = router.query?.purchaseSeatTab;
    const eventCodes = router.query?.eventCodes;
    const isChooseSeatsTab =
      purchaseSeatTab === purchaseSeatsTabUIDs.chooseSeats;
    return isChooseSeatsTab && eventCodes;
  };

  const handleLogoutFromChooseSeatsTab = async () => {
    dispatch(isAuthenticatingState.actions.set(true));
    let state: any = {
      authOrigin: authOriginUIDs.purchaseSeats,
      pathname: routes.purchaseSeats,
      query: router.query,
    };
    state = window.btoa(JSON.stringify(state)).replaceAll("=", "");
    const logoutRedirectUrl = `${globalConfig.frontendUrl}/${teamSchema}/auth?authType=login&state=${state}`;
    logoutTicketMasterV2(logoutRedirectUrl);
  };

  const handleSessionTimeoutLogout = async () => {
    setIsLogginOutTm(true);

    if (platform === vendorUUIDs.ticketmaster) {
      if (isChooseSeatsTabWithEventCodes()) {
        await handleLogoutFromChooseSeatsTab();
      } else {
        await logoutTicketMaster();
      }
    }

    if (!!oktaIssuer && !!oktaClientId) {
      if (shouldUseInternalLogin) return handleInternalLogOut();

      await handleSignOut("/");
    }
  };

  const handleUserPing = async () => {
    try {
      await userPing.mutateAsync();
    } catch (error) {
      console.log(error);
    }
  };

  const handleInterceptApiResponse = () => {
    api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error?.response?.status === 401) {
          handleSessionTimeoutLogout();
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  };

  const handleAutoLogout = () => {
    if (user && authConfigs?.isAutoLogoutEnabled) {
      handleSessionTimeoutLogout();
    }
  };

  useEffect(() => {
    if (!user || !user?.userId) return;
    handleInterceptApiResponse();
    handleUserPing();
    handleAutoLogout();
    const pingInterval = setInterval(handleUserPing, 1000 * 60 * 30);
    // return () => clearInterval(pingInterval);
  }, [user]);

  React.useEffect(() => {
    if (urlHasSessionTimeoutParam) sessionTimeoutModal.onOpen();
  }, [urlHasSessionTimeoutParam]);

  useEffect(() => {
    dispatch(userState.actions.check({ teamSchema }));
  }, []);

  useEffect(() => {
    if (
      user &&
      (!user?.version || user?.version !== globalConfig?.logOutVersion)
    ) {
      handleSessionTimeoutLogout();
    }
  }, [user]);

  return (
    <>
      <SessionTimeoutModal
        isOpen={sessionTimeoutModal.open}
        onClose={() => {
          const routerQueryUpdated = { ...router?.query };
          delete routerQueryUpdated["isSessionTimeout"];
          router?.push({ query: routerQueryUpdated });
          sessionTimeoutModal.onClose();
        }}
      />
      <AuthContext.Provider
        value={{ logoutTicketMaster, logoutTicketMasterV2 }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
}
